import { InputSize } from "@galaxy-io/dls/inputs/Input";
import Box from "@galaxy-io/dls/layout/Box";
import { MenuItem, MenuItemVariant } from "@galaxy-io/dls/menu/Menu";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import type { Tag } from "@gtm/contracts/core/tags";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { PageHeader } from "@/components/workspace/page-header";
import { FeedViewport, Inline } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { apiClient, unwrap } from "@/lib/api-client";
import { useTags } from "./tag-controls";
import { TagEditor } from "./tag-editor";
import { TagRulesView } from "./tag-rules-view";

export function TagsView() {
  const tags = useTags();
  const { params, update } = useUrlState();
  const q = params.get("q") || "";
  const rules = params.get("view") === "rules";
  const [creatingRule, setCreatingRule] = useState(false);
  const items = (tags.data ?? []).filter((tag) => tag.name.toLowerCase().includes(q.toLowerCase()));
  const [editing, setEditing] = useState<Tag | "new" | null>(null);
  const retainedEditing = useRetainedValue(editing);
  const [removing, setRemoving] = useState<Tag | null>(null);
  const client = useQueryClient();
  async function remove() {
    if (!removing) return;
    await unwrap(apiClient().DELETE("/tags/{id}", { params: { path: { id: removing.id } } }));
    setRemoving(null);
    void client.invalidateQueries();
  }
  return (
    <>
      <PageHeader
        title="Tags"
        actions={
          <Button
            label={rules ? "Create tag rule" : "Create tag"}
            onClick={() => (rules ? setCreatingRule(true) : setEditing("new"))}
          />
        }
      />
      <Box padding={[0, 12]}>
        <Tabs
          ariaLabel="Tag sections"
          value={rules ? "rules" : "tags"}
          onChange={(value) => {
            setCreatingRule(false);
            update({ view: value === "rules" ? "rules" : null, q: null });
          }}
          items={[
            { id: "tags", label: "Tags" },
            { id: "rules", label: "Tag rules" },
          ]}
        />
      </Box>
      {rules ? (
        <TagRulesView creating={creatingRule} onCloseCreate={() => setCreatingRule(false)} />
      ) : (
        <>
          <BaseToolbar
            leadingActions={
              <Input
                aria-label="Search tags"
                placeholder="Search tags…"
                value={q}
                onChange={(event) => update({ q: event || null }, true)}
                type="search"
                size={InputSize.SMALL}
                label="Search tags"
                hideLabel
              />
            }
          />
          <FeedViewport>
            <RecordTable
              rowActions={(row) => row.actions}
              label="Tags"
              isLoading={tags.isPending}
              error={tags.error || undefined}
              onRetry={() => void tags.refetch()}
              rowKeys={items.map((tag) => tag.id)}
              columns={[
                { key: "name", label: "Tag", width: "auto" },
                { key: "records", label: "Tagged records", width: 240 },
              ]}
              rows={items.map((tag) => ({
                name: tag.name,
                records: (
                  <Inline>
                    <Link href={`/people?tags=${tag.id}`}>People</Link>
                    <Link href={`/companies?tags=${tag.id}`}>Companies</Link>
                  </Inline>
                ),
                actions: (
                  <>
                    <MenuItem label="Rename" onSelect={() => setEditing(tag)} />
                    <MenuItem
                      label="Delete"
                      variant={MenuItemVariant.ERROR}
                      onSelect={() => {
                        setRemoving(tag);
                      }}
                    />
                  </>
                ),
              }))}
            />
          </FeedViewport>
        </>
      )}
      <TagEditor
        open={!!editing}
        tag={retainedEditing === "new" ? null : (retainedEditing ?? null)}
        onClose={() => setEditing(null)}
      />
      <ConfirmAction
        isOpen={!!removing}
        header={`Delete ${removing?.name || "tag"}?`}
        description={
          <>
            This tag will be removed from all people and companies. The records themselves will
            remain. Any rules assigning this tag will also stop applying.
          </>
        }
        label="Delete tag"
        isDestructive
        onOpenChange={(next) => {
          if (!next) (() => setRemoving(null))();
        }}
        onConfirm={async () => remove()}
      />
    </>
  );
}
